<template>
  <div class="position-relative">
    <div v-if="hideFullPageSpinner && isLoadingDetails" class="inner-spinner">
      <BSpinner variant="primary" />
    </div>
    <div v-if="!user && loadError" id="user_details_load_error" class="card">
      <b-card-body>
        <b-alert show variant="danger" class="mb-1">
          <div class="p-1">
            <h4 class="alert-heading">User details unavailable</h4>
            <p class="mb-0">{{ loadError }}</p>
          </div>
        </b-alert>
        <b-button variant="primary" @click="getUserDetails">Try again</b-button>
      </b-card-body>
    </div>
    <div v-if="user" id="user_details_page">
      <div class="card">
        <quick-links v-if="shownAsModal" class="box-shadowed" :useActiveLink="false" />
        <b-card-header>
          <b-alert v-if="staffReviewer" :show="$store.state.auth.AppActiveAdmin.id !== staffReviewer.id" variant="warning" class="p-1 mb-2 border border-warning">
            <div class="d-flex text-dark align-items-center">
              <UserBadgeName :name="staffReviewer.name" :color="staffReviewer.hex_color" />
              <div class="text-bold pl-25">is looking at this user</div>
            </div>
          </b-alert>
          <div
              style="
            display: flex;
            justify-content: space-between;
            width: 100%;
            gap: 20px;
          "
          >
            <div>
              <div class="d-flex align-items-center justify-content-between">
                <h3 class="mb-0">User Details</h3>
                <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="showUserProfilingBasics(userId)"
                >
                  Basic Profiling
                </b-button>
              </div>
              <table class="table b-table table-striped table-hover mt-1">
                <tbody>
                <tr>
                  <td>User Id</td>
                  <td>{{ user.id }}</td>
                </tr>
                <tr>
                  <td>App</td>
                  <td class="d-flex">
                    <AppElement :id="user.app_id" :name="user.app_name" />
                  </td>
                </tr>
                <tr v-if="user.is_po">
                  <td>PO User ID</td>
                  <td class="d-flex text-nowrap align-items-center" style="gap: 5px">
                    <b-img
                        :src="primeOpinionLogo"
                        alt="prime-opinion-logo"
                        class="mr-50"
                        width="20px"
                    ></b-img>
                    <POUserElement :id="user.po_user_id" :additionalText="user.po_user_deleted ? '(Deleted)' : ''" target="_blank" />
                  </td>
                </tr>
                <tr v-if="user.is_fs">
                  <td>FS User ID</td>
                  <td class="d-flex text-nowrap align-items-center" style="gap: 5px">
                    <b-img
                        :src="fiveSurveysLogo"
                        alt="five-surveys-logo"
                        class="mr-50"
                        width="25px"
                    ></b-img>
                    <FSUserElement :id="user.fs_user_id" :additionalText="user.fs_user_deleted ? '(Deleted)' : ''" target="_blank" />
                  </td>
                </tr>
                <tr v-if="user.is_hc">
                  <td>HC User ID</td>
                  <td class="d-flex text-nowrap align-items-center" style="gap: 5px">
                    <b-img
                        :src="heyCashLogo"
                        alt="hey-cash-logo"
                        class="mr-50"
                        width="25px"
                    ></b-img>
                    <HCUserElement :id="user.hc_user_id" :additionalText="user.hc_user_deleted ? '(Deleted)' : ''" target="_blank" />
                  </td>
                </tr>
                <tr v-if="user.is_ts">
                  <td>TS User ID</td>
                  <td class="d-flex text-nowrap align-items-center" style="gap: 5px">
                    <b-img
                        :src="topSurveysLogo"
                        alt="top-surveys-logo"
                        class="mr-50"
                        width="25px"
                    ></b-img>
                    <TSUserElement :id="user.ts_user_id" :additionalText="user.ts_user_deleted ? '(Deleted)' : ''" target="_blank" />
                  </td>
                </tr>
                <tr v-if="user.is_es">
                  <td>ES User ID</td>
                  <td class="d-flex text-nowrap align-items-center" style="gap: 5px">
                    <b-img
                        :src="earnStarLogo"
                        alt="earn-star-logo"
                        class="mr-50"
                        width="25px"
                    ></b-img>
                    <ESUserElement :id="user.es_user_id" :additionalText="user.es_user_deleted ? '(Deleted)' : ''" target="_blank" />
                  </td>
                </tr>
                <tr v-if="user.is_pt">
                  <td>PT User ID</td>
                  <td class="d-flex text-nowrap align-items-center" style="gap: 5px">
                    <b-img
                        :src="paidTesterLogo"
                        alt="paid-tester-logo"
                        class="mr-50"
                        width="25px"
                    ></b-img>
                    <PTUserElement :id="user.pt_user_id" :additionalText="user.pt_user_deleted ? '(Deleted)' : ''" target="_blank" />
                  </td>
                </tr>
                <tr>
                  <td>Platform</td>
                  <td class="d-flex">
                    <span v-if="platformLastHundredClicks" id="platform-device-icon-wrapper" class="d-inline-block">
                      <PlatformDeviceIcon
                          :platform="platformLastHundredClicks"
                          style="pointer-events: none"
                      />
                      <b-tooltip target="platform-device-icon-wrapper" triggers="hover" placement="bottomright">
                        <div class="text-left">
                          Last 100 clicks: <strong>{{ platformLastHundredClicks }}</strong><br>
                          Profile Device: <strong>{{ user.device }}</strong>
                        </div>
                      </b-tooltip>
                    </span>
                    <span v-else></span>
                  </td>
                </tr>
                <tr>
                  <td>User Hash</td>
                  <td>{{ user.hash }}</td>
                </tr>
                <tr>
                  <td>External Id</td>
                  <td v-if="showMinimalizeExternalId">
                    {{ user.external_user_id.substr(0, 50) }}<span v-if="user.external_user_id.length > 50" class="text-primary" @click="showMinimalizeExternalId = false">...</span>
                  </td>
                  <td v-else>{{ user.external_user_id }}</td>
                </tr>
                <tr>
                  <td>External Source</td>
                  <td>{{ user.external_source }}</td>
                </tr>
                <tr>
                  <td>E-Mail</td>
                  <td class="text-nowrap">
                    <div class="d-flex align-items-center">
                      <template v-if="emailDetails.name && emailDetails.domain">
                        {{ emailDetails.name }}@{{ emailDetails.domain }}
                      </template>
                      <b-badge v-if="isHighRiskEmailDomain" variant="danger">High-Risk Email</b-badge>
                      <!-- Show a clickable badge for breaches if there is real data -->
                    </div>

                    <div v-if="user.email" class="d-flex align-items-center mt-50" style="gap: 5px">
                      <b-button
                          v-if="emailBreaches"
                          :variant="breachesButtonColor"
                          size="sm"
                          class="cursor-pointer"
                          @click="handleClickBreaches"
                      >
                        {{ breachesButtonText }}
                      </b-button>

                      <!-- Show a clickable badge for connected accounts if sum > 0 -->
                      <b-button
                          v-if="connectedAccounts"
                          :variant="connectedButtonColor"
                          size="sm"
                          class="cursor-pointer"
                          @click="handleClickAccountsConnected"
                      >
                        {{ connectedBadgeText }}
                      </b-button>

                      <!-- Show 'X Years old' if the backend returned a numeric age -->
                      <b-button
                          v-if="emailBreaches"
                          size="sm"
                          :variant="emailAgeColorClass"
                      >
                        {{ emailAgeText }}
                      </b-button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>Country</td>
                  <td>
                    <CountryElement
                        :code="user.country"
                        :showHighRiskIndicator="isUserCountryHighRisk"
                        :name="user.country_name"
                    />
                  </td>
                </tr>
                <tr>
                  <td>Languages</td>
                  <td>
                    {{ user.languages.map((language) => language.language).join(", ") }}
                  </td>
                </tr>
                <tr>
                  <td>Interface Languages</td>
                  <td>{{ languageCodes[user.interface_language] }}</td>
                </tr>
                <tr>
                  <td>Status</td>
                  <td style="line-height: 20px; position: relative">
                    <div class="row">
                      <div class="col-11">
                        <template v-if="user.status === 1">
                          <b-badge style="font-size: 11px" variant="success"
                          >Active
                          </b-badge>
                          <template v-if="user.protected_status > 0">
                            <br/>
                            <span>
                              <b>Protect: </b>
                              <span v-if="user.protected_status === 2"
                              >until
                                {{
                                  user.protected_until | dotDateTimeShort
                                }}
                                (<span
                                    :class="
                                    user.status_left_time <= 2
                                      ? 'text-warning'
                                      : ''
                                  "
                                >{{ user.status_left_time }}</span
                                >
                                days left)</span
                              >
                              <span v-if="user.protected_status === 1"
                              >infinite</span
                              >
                              <span v-if="user.protected_status === 3">
                                <span v-if="user.protected_completes > 0">
                                  {{ `For ${user.protected_completes} completes` }}
                                  (<span :class="user.completes_left <= 5 ? 'text-warning' : ''">
                                    {{ user.completes_left }} left
                                  </span>)
                                </span>

                                <span
                                    v-if="user.protected_offers_payout > 0"
                                    :style="user.protected_completes > 0 ? 'display: block;' : ''"
                                >
                                  <b v-if="user.protected_completes > 0">Protect: </b>
                                  {{ `For $${user.protected_offers_payout} Offer Payout` }}
                                  (<span :class="user.offer_payout_left <= 5 ? 'text-warning' : ''">
                                    ${{ user.offer_payout_left }} left
                                  </span>)
                                </span>
                              </span>
                            </span>
                          </template>
                        </template>
                        <template v-if="user.status === 2">
                          <b-badge
                              style="font-size: 11px"
                              variant="danger"
                          >Banned
                          </b-badge>
                          <br/>
                          <b>Until: </b>
                          <span v-if="user.banned_until">
                            {{ user.banned_until | dotDateTime }}</span
                          >
                          <span v-else>Infinite</span>
                        </template>
                        <template v-if="user.status === 3">
                          <b-badge
                              class="user-status-3"
                              style="font-size: 11px"
                          >Shadow Banned
                          </b-badge>
                          <br/>
                          <b>Until: </b>
                          <span v-if="user.banned_until">
                            {{ user.banned_until | dotDateTime }}</span
                          >
                          <span v-else>Infinite</span>
                        </template>
                        <template v-if="user.status === 4">
                          <b-badge
                              style="font-size: 11px"
                              variant="warning"
                          >Review
                          </b-badge>
                        </template>
                        <template v-if="user.status === 5">
                          <b-badge
                              class="user-status-5"
                              style="font-size: 11px"
                          >Shadow Review
                          </b-badge>
                        </template>
                        <template v-if="user.status === 6">
                          <b-badge
                              class="user-status-6"
                              style="font-size: 11px"
                          >On Hold
                          </b-badge>
                          <br/>
                          <b>Until: </b>
                          <span v-if="user.banned_until">
                            {{ user.banned_until | dotDateTime }}</span
                          >
                          <span v-else>Infinite</span>
                        </template>
                        <template v-if="user.status_updated_at">
                          <br/>
                          <b>Last update: </b
                          >{{ user.status_updated_at | dotDateTime }}&nbsp;
                          <template v-if="user.status_by_admin_id">
                            <div class="d-inline-flex">
                              by &nbsp;
                              <UserBadgeName
                                  :color="user.admin_hex_color"
                                  :name="user.admin_name"
                              />
                            </div>
                          </template>
                        </template>
                        <template v-if="user.banned_message">
                          <br/>
                          <b>Comment: </b>{{ user.banned_message }}
                        </template>
                      </div>

                    </div>
                  </td>
                </tr>
                </tbody>
                <tfoot>
                <tr>
                  <td class="text-nowrap" style="padding-left: 0px">
                    Created
                    {{ user.created_at | dotDateTime }}
                  </td>
                  <td
                      class="text-nowrap"
                      style="padding-right: 0px; text-align: right"
                  >
                    <div v-if="user.last_click">
                      Last click
                      {{ user.last_click.created_at | dotDateTime }}
                    </div>
                    <div v-else>
                      <i>No click data yet</i>
                    </div>
                  </td>
                </tr>
                </tfoot>
              </table>


            </div>
            <div style="min-width: 30%">
              <div>
                <h3>Performance</h3>
                <table
                    class="table b-table table-striped table-hover"
                    style="min-width: 200px"
                >
                  <tbody>
                  <tr>
                    <td>Clicks</td>
                    <td>{{ performance.total_clicks }}</td>
                  </tr>
                  <tr>
                    <td>Completes</td>
                    <td>{{ performance.total_completes }}</td>
                  </tr>
                  <tr>
                    <td>Conversion</td>
                    <td>{{ performance.conversion | round2Decimal }} %</td>
                  </tr>
                  <tr>
                    <td>Survey Revenue</td>
                    <td>{{ performance.survey_revenue | formatCurrency }}</td>
                  </tr>
                  <tr>
                    <td>Survey Publisher Payout</td>
                    <td>{{ user.survey_publisher_payout | formatCurrency }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div class="mt-5">
                <h3>Connected Accounts</h3>
                <div style="overflow-x: auto; max-width: 100%;">
                  <table
                      class="table b-table table-striped table-hover"
                      style="min-width: 250px"
                  >
                    <thead>
                    <tr>
                      <th>User ID</th>
                      <th>App</th>
                      <th>Pub. Payout</th>
                      <th>IAP Data</th>
                      <th>Attribute</th>
                      <th>Match</th>
                    </tr>
                    </thead>
                    <tbody>
                    <template v-if="limitedConnectedAccounts.length === 0">
                      <tr>
                        <td colspan="6" class="text-center">
                          Recent IP results load on demand. Use the action below.
                        </td>
                      </tr>
                    </template>
                    <template v-else>
                      <tr v-for="account in limitedConnectedAccounts" :key="account.user_id">
                        <td style="max-width: 100px">
                          <UserElement :id="account.user_id" :status="account.status"/>
                        </td>
                        <td>
                          <AppWithLogo :appName="account.app_name" />
                        </td>
                        <td style="white-space: nowrap">{{ account.publisher_payout_sum | newFormatCurrencyDecimals }}</td>
                        <td style="white-space: nowrap">{{ account.iap_data | newFormatCurrencyDecimals }}</td>
                        <td>{{ account.formatted_matching_attributes }}</td>
                        <td :class="getProbabilityClass(account.matching_probability)">
                          {{ account.matching_probability }}%
                        </td>
                      </tr>
                    </template>
                    <tr>
                      <td>
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          @click="openRelatedAccountsModal()"
                        >
                          View
                        </b-button>
                      </td>
                      <td
                          colspan="5"
                          style="font-size: 12px"
                          :class="[
                        hasRelatedAccountsSummary && relatedAccounts.summary.flagged_rate && parseFloat(relatedAccounts.summary.flagged_rate) >= 0.2 ? 'bg-danger' : '',
                        'text-center',
                      ]"
                      >
                        <strong v-if="hasRelatedAccountsSummary">
                          {{ relatedAccounts.summary.flagged_users }}/{{ relatedAccounts.summary.total_users }} banned
                          ({{ relatedAccounts.summary.flagged_rate ? relatedAccounts.summary.flagged_rate : 0 | percentageRound }})
                        </strong>
                        <span v-else class="text-muted">Recent IP filters load on demand.</span>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
            <div style="min-width: 23%">
              <div class="d-flex flex-column align-items-start mb-1">
                <div class="mb-auto w-100">
                  <h3>Publisher Payout</h3>

                  <div class="p-chart-container">
                    <vue-apex-charts
                        type="bar"
                        height="250"
                        :series="publisherPayoutChart.series"
                        :options="publisherPayoutChartOptions"
                    ></vue-apex-charts>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </b-card-header>

      </div>
      <div class="card">
        <b-card-header>
          <div
              style="
            display: flex;
            justify-content: flex-start;
            width: 100%;
            gap: 5%;
          "
          >
            <div style="min-width: 25%">
              <div class="mb-3">
                <h3>Reconciliations</h3>
                <table
                    class="table b-table table-striped table-hover"
                    style="max-width: 160px"
                >
                  <thead>
                  <tr>
                    <th>Month</th>
                    <th>Stats</th>
                    <th>Current</th>
                    <th>Projected</th>
                    <th>Confidence</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr
                      v-for="(
                      reconciliation, iReconciliation
                    ) in reconciliation.data"
                      :key="iReconciliation"
                  >
                    <td style="width: 60px">{{ reconciliation.month }}</td>
                    <td class="text-nowrap" style="width: 45px">
                      <span class="text-danger">{{
                          reconciliation.reconciled_count
                        }}</span>
                      <span>/ {{ reconciliation.completes_count }}</span>
                      <span
                          v-if="reconciliation.high_recon_surveys_excluded > 0"
                      >
                        <b-icon
                            :id="`tooltip-high-recon-surveys-excluded-${iReconciliation}`"
                            class="border border-black mb-25 ml-50"
                            icon="star-fill"
                            scale=".85"
                        ></b-icon>
                        <b-tooltip
                            :target="`tooltip-high-recon-surveys-excluded-${iReconciliation}`"
                            triggers="hover"
                        >
                          <div class="font-small-4">
                            {{
                              reconciliation.high_recon_surveys_excluded
                            }}
                            surveys excluded for high recon
                          </div>
                        </b-tooltip>
                      </span>
                    </td>
                    <td
                        :class="
                        getClassReconciliationPercentage(
                          reconciliation.current * 100
                        )
                      "
                    >
                      <span>
                        {{ reconciliation.current | percentageRound }}</span
                      >
                    </td>
                    <td
                        v-if="reconciliation.is_show_projected"
                        :class="
                        getClassReconciliationPercentage(
                          reconciliation.projected * 100
                        )
                      "
                        class="text-nowrap"
                    >
                      <span v-if="reconciliation.projected"
                      >≈
                        {{ reconciliation.projected | percentageRound }}</span
                      >
                    </td>
                    <td v-else></td>
                    <td
                        v-if="reconciliation.is_show_projected"
                        :class="
                        getConfidence(
                          reconciliation.avg_days_since_complete
                        ).cssClass
                      "
                        class="text-nowrap"
                    >
                      <span v-if="reconciliation.avg_days_since_complete"
                      >
                        {{ getConfidence(reconciliation.avg_days_since_complete).text }}</span
                      >
                    </td>
                    <td v-else></td>
                  </tr>
                  <tr v-if="reconciliation.total > 0">
                    <td class="text-nowrap" colspan="2">
                      Reconciliations
                    </td>
                    <td
                        :class="
                        getClassReconciliationPercentage(reconciliation.total)
                      "
                        class="text-nowrap text-center"
                        colspan="2"
                    >
                      {{ reconciliation.total }} %
                    </td>
                  </tr>
                  <tr>
                    <td v-if="isLoadingLifetime" colspan="5">
                      <b-spinner
                          small
                          v-if="isLoadingLifetime"
                          label="Loading..."
                          class="mr-1"
                      />
                    </td>
                    <td v-if="!isLoadingLifetime" colspan="2" class="text-left">
                      <div class="text-nowrap">
                        Lifetime Reconciliation
                      </div>
                    </td>
                    <td v-if="!isLoadingLifetime" colspan="2" class="text-center">
                      {{ totalLifetimePercentage }}%
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <h3 class="mb-1">Quality Questions</h3>

              <div v-for="(qualityQuestion, iQualityQuestion)  in quality_questions" :key="iQualityQuestion.id" class="mb-2">
                <div v-if="qualityQuestion.open_end">
                  <h5>{{ qualityQuestion.open_end.question_text }}</h5>

                  <div v-if="qualityQuestion.open_end.answer_text.includes('/cp')" class="text-right mb-50"><span class="p-50 bg-danger font-weight-bold">Copy/Pasted</span></div>
                  <div
                      style="
                      padding: 5px 10px;
                      border: 1px solid #c3c3c3;
                      min-height: 60px;
                    "
                  >
                    {{ qualityQuestion.open_end.answer_text.replace('/cp', '') }}
                  </div>

                  <div class="row">
                    <div class="col-4 d-flex align-items-center">
                      <div
                          v-if="qualityQuestion.open_end.duplicate_count || qualityQuestion.open_end.similar_count"
                          class="text-nowrap d-flex align-items-center mb-2"
                      >
                        <span class="mr-1"><b>{{ qualityQuestion.open_end.duplicate_count }}</b> Identical</span>
                        <span><b>{{ qualityQuestion.open_end.similar_count }}</b> Similar</span>
                      </div>
                      <span v-else class="text-nowrap d-flex align-items-center mb-2">No similar and duplicate</span>
                    </div>
                    <div class="col-8 d-flex align-items-center">
                      <div class="col-6 mb-2 font-weight-bold d-flex align-items-center">Banned Open End Duplicates</div>
                      <div v-if="qualityQuestion.open_end.similar_count > 0 || qualityQuestion.open_end.duplicate_count > 0"
                           :class="getClassBannedUserDuplicates(qualityQuestion.open_end)"
                           class="col-6 p-50 mb-2 d-flex align-items-center"
                      >
                        {{ qualityQuestion.open_end.banned_users_count_percentage | percentage }}
                        ({{ qualityQuestion.open_end.banned_users_duplicate_count }})
                      </div>
                      <div v-else class="p-50 mb-2 d-flex align-items-center">-</div>
                    </div>
                  </div>
                </div>

                <div v-else>
                  <h5>{{ qualityQuestion.question_text }}?</h5>

                  <div>
                    <span v-for="(answer, iAnswer) in qualityQuestion.answers" :key="iAnswer" :class="{'text-primary': answer.is_answered}"><span v-if="iAnswer > 0">, </span>{{ answer.answer_text }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3>SMS Verification</h3>
              <table
                  v-for="phone_verification in phone_verifications"
                  :key="phone_verification.phone"
                  class="table b-table table-striped table-hover mb-1"
              >
                <tbody>
                <tr :key="phone_verification.phone" class="mb-1">
                  <td
                      class="text-center text-nowrap"
                      rowspan="4"
                      style="width: 130px"
                  >
                    <div class="block mb-1">
                      {{ phone_verification.phone || "---" }}
                    </div>
                    <b-badge
                        v-if="phone_verification.verified"
                        style="font-size: 11px"
                        variant="success"
                    >
                      Verified
                    </b-badge>
                    <b-badge
                        v-if="!phone_verification.verified"
                        style="font-size: 11px"
                        variant="secondary"
                    >
                      Not verified
                    </b-badge>
                    <div class="mt-2">{{ phone_verification.carrier }}</div>
                  </td>
                  <td class="text-nowrap">Verification Delay</td>
                  <td
                      :class="getClassVerificationDelay(phone_verification.delay)"
                      class="text-nowrap"
                  >
                    <span v-if="phone_verification.delay">
                      {{ phone_verification.delay | toHourMinuteSecond }}
                    </span>
                    <span v-else>---</span>
                  </td>
                </tr>

                <tr>
                  <td class="text-nowrap">Copy/Pasted</td>
                  <td
                      v-if="phone_verification.phone"
                      :class="
                      phone_verification.is_copy_pasted
                        ? 'bg-danger'
                        : 'bg-success'
                    "
                      class="text-nowrap"
                  >
                    <span class="text-white">{{
                        phone_verification.is_copy_pasted ? "True" : "False"
                      }}</span>
                  </td>
                  <td v-else></td>
                </tr>

                <tr>
                  <td class="text-nowrap">IPQS</td>
                  <td
                      v-if="phone_verification.phone"
                      :class="getClassIpqsScore(phone_verification.ipqs_score)"
                      class="text-nowrap"
                  >
                    {{ phone_verification.ipqs_score }}
                  </td>
                  <td v-else></td>
                </tr>

                <tr>
                  <td class="text-nowrap">
                    Carrier Risk
                  </td>
                  <td
                      v-if="phone_verification.phone"
                      :class="
                      getClassCarrierRisk(phone_verification.banned_percentage)
                    "
                      class="text-nowrap"
                  >
                    {{ phone_verification.banned_percentage | percentageRound }}
                  </td>
                  <td v-else></td>
                </tr>
                </tbody>
              </table>
              <div class="bg-secondary text-white p-1" v-if="!phone_verifications.length && !user.app_supports_verification">
                Verification not supported for this app
              </div>
            </div>
          </div>
        </b-card-header>
      </div>


      <b-card>
        <div class="p-chart-container">
          <div class="p-chart-header">
            Click Completes and Conversion Rates in 30 days
          </div>
          <vue-apex-charts
              :options="completesAndConversionsChartOptions"
              :series="completes_and_conversion_rates.series"
              height="450"
              type="line"
          ></vue-apex-charts>
        </div>
      </b-card>
      <b-modal ref="user_related_account_data" size="xl" title="Connected Accounts" hide-footer>
        <div class="d-flex align-items-center justify-content-between flex-wrap mb-1" style="gap: 0.75rem;">
          <div class="d-flex flex-wrap" style="gap: 0.5rem;">
            <b-button
              v-for="days in relatedAccountDayOptions"
              :key="`related-accounts-${days}`"
              size="sm"
              :variant="selectedRelatedAccountsDays === days ? 'primary' : 'outline-primary'"
              @click="changeRelatedAccountsDays(days)"
            >
              View all ({{ days }} days)
            </b-button>
          </div>
          <small class="text-muted">
            Recent IP: last {{ selectedRelatedAccountsDays }} days
          </small>
        </div>
        <div v-if="isRelatedAccountsModalLoading" class="py-3 text-center">
          <b-spinner variant="primary" label="Loading related accounts..." />
        </div>
        <div v-else-if="relatedAccountsModalError" class="py-2 text-center text-danger">
          {{ relatedAccountsModalError }}
        </div>
        <RelatedAccountData
          v-else-if="modalRelatedAccountsResults.length"
          :relatedAccountData="modalRelatedAccountsResults"
        />
        <p v-else class="mb-0 py-2 text-center">
          No related accounts found
        </p>
      </b-modal>
      <!-- MODAL: Breaches list -->
      <b-modal ref="breachesModal" title="Breaches" hide-footer>
        <p v-if="emailBreaches && emailBreaches.breach_list">
          {{ emailBreaches.breach_list }}
        </p>
        <p v-else>No breach details available</p>
      </b-modal>

      <!-- MODAL: Connected Accounts list -->
      <b-modal ref="accountsConnectedModal" title="Connected Accounts" hide-footer>
        <ul v-if="connectedAccounts && connectedAccounts.accounts && connectedAccounts.accounts.length">
          <li v-for="(acc, idx) in connectedAccounts.accounts" :key="idx">
            {{ acc }}
          </li>
        </ul>
        <p v-else>No connected accounts found</p>
      </b-modal>

      <b-modal
          ref="user_profiling_basics_modal"
          size="lg"
          title="User Profiling Basics"
      >
        <UserProfilingBasics :userProfilingBasics="userProfilingBasics"/>
      </b-modal>


    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import UserBadgeName from '@/views/users/UserBadgeName.vue'
import {$themeConfig} from '@themeConfig'
import POUserElement from '@/views/prime_opinion/elements/POUserElement.vue'
import FSUserElement from '@/views/five_surveys/elements/FSUserElement.vue'
import HCUserElement from '@/views/hey_cash/elements/HCUserElement.vue'
import TSUserElement from '@/views/top_surveys/elements/TSUserElement.vue'
import ESUserElement from '@/views/earn_star/elements/ESUserElement.vue'
import PTUserElement from '@/views/paid_tester/elements/PTUserElement.vue'
import QuickLinks from '@/main/custom-components/QuickLinks.vue'
import store from '@/store'
import PlatformDeviceIcon from '@/main/custom-components/PlatformDeviceIcon.vue'
import RelatedAccountData from '@/views/users/UserDetails/RelatedAccountData.vue'
import AppElement from '@/views/elements/AppElement.vue'
import probabilityMixin from '@/views/users/mixins/probabilityMixin'
import AppWithLogo from '@/views/users/UserDetails/AppWithLogo.vue'
import UserProfilingBasics from '@/views/users/UserDetails/UserProfilingBasics.vue'

const RELATED_ACCOUNT_DAY_OPTIONS = [7, 14, 30]
const DEFAULT_RELATED_ACCOUNT_DAYS = 7

export default {
  name: 'BitburstUserDetails',
  mixins: [probabilityMixin],
  props: {
    id: [Number, String],
    status: Number,
    shownAsModal: Boolean,
    hideFullPageSpinner: Boolean
  },
  components: {
    AppWithLogo,
    ESUserElement,
    TSUserElement,
    AppElement,
    HCUserElement,
    RelatedAccountData,
    PlatformDeviceIcon,
    QuickLinks,
    FSUserElement,
    POUserElement,
    UserBadgeName,
    VueApexCharts,
    PTUserElement,
    UserProfilingBasics
  },
  emits: ['update-user-settings', 'update-user-status'],
  data () {
    return {
      primeOpinionLogo: null,
      fiveSurveysLogo: null,
      heyCashLogo: null,
      topSurveysLogo: null,
      earnStarLogo: null,
      paidTesterLogo: null,
      user: null,
      loadError: null,
      performance: null,
      quality_questions: [],
      completes_and_conversion_rates: null,
      phone_verifications: null,
      reconciliation: {},
      otherAccounts: [],
      relatedAccounts: {},
      relatedAccountData: [],
      relatedAccountDayOptions: RELATED_ACCOUNT_DAY_OPTIONS,
      selectedRelatedAccountsDays: DEFAULT_RELATED_ACCOUNT_DAYS,
      relatedAccountsModalData: null,
      relatedAccountsModalError: null,
      isRelatedAccountsModalLoading: false,
      relatedAccountsModalRequestId: 0,
      languageCodes: [],
      showMinimalizeExternalId: true,
      isHighRiskEmailDomain: false,
      platformLastHundredClicks: null,
      staffReviewer: null,
      publisherPayoutChart: {
        series: [],
        label: []
      },
      emailBreaches: null,
      connectedAccounts: null,
      lifetimeReconciliation: [],
      isLoadingLifetime: false,
      totalLifetimePercentage: null,
      isLoadingDetails: false,
      userProfilingBasics: []
    }
  },
  computed: {
    userId () {
      return this.id ? this.id : this.$route.params.userId
    },
    isUserCountryHighRisk () {
      return !!this.user?.is_high_risk_country
    },
    emailDetails () {
      if (this.user.email) {
        const [name, domain] = this.user.email.split('@')
        return { ...this.user.email, name, domain }
      } else {
        return { ...this.user.email, name: null, domain: null }
      }
    },
    completesAndConversionsChartOptions () {
      return {
        theme: {
          mode: this.$store.state.appConfig.layout.skin
        },
        chart: {
          height: 350,
          type: 'line'
        },
        stroke: {
          width: [0, 4]
        },
        dataLabels: {
          enabled: true,
          enabledOnSeries: [1]
        },
        tooltip: {
          x: {
            format: 'dddd, dd MMM'
          }
        },
        xaxis: {
          type: 'datetime',
          categories: this.completes_and_conversion_rates.labels
        },
        yaxis: [
          {
            title: {
              text: 'Completes'
            },
            labels: {
              formatter (val) {
                return val.toFixed(0)
              }
            }
          },
          {
            opposite: true,
            title: {
              text: 'Conversion Rate'
            }
          }
        ]
      }
    },
    publisherPayoutChartOptions () {
      return {
        theme: {
          mode: this.$store.state.appConfig.layout.skin
        },
        chart: {
          type: 'bar',
          stacked: true
        },
        colors: ['#20c997', '#ff69b4'], // Series 1: green teal, Series 2: pink
        dataLabels: {
          enabled: false
        },
        tooltip: {
          x: {
            format: 'dddd, dd MMM'
          },
          y: {
            formatter (val) {
              return (val < 0 ? '-$' : '$') + Math.abs(val).toFixed(2)
            }
          }
        },
        xaxis: {
          type: 'datetime',
          categories: this.publisherPayoutChart.labels
        },
        yaxis: [
          {
            labels: {
              formatter (val) {
                return val.toFixed(1)
              }
            }
          }
        ]
      }
    },
    breachesButtonText () {
      if (!this.emailBreaches || this.emailBreaches.count === 0) return 'Check Breaches'
      // Only row is NoBreach
      if (this.emailBreaches.count === 1 && this.emailBreaches.breach_list === 'NoBreach') {
        return 'No Breaches'
      }
      // Otherwise => X Breaches
      return `${this.emailBreaches.count} Breaches`
    },
    breachesButtonColor () {
      // No data => grey
      if (!this.emailBreaches || this.emailBreaches.count === 0) return 'secondary'
      if (this.emailBreaches.count === 1 && this.emailBreaches.breach_list === 'NoBreach') return 'danger'
      if (this.emailBreaches.count >= 3) return 'success'
      return 'warning'
    },
    connectedBadgeText () {
      if (!this.connectedAccounts || this.connectedAccounts.status === 'not_checked') return 'Check Connected Accounts'
      if (this.connectedAccounts.status === 'no_connected') return 'No Connected Accounts'
      return `${this.connectedAccounts.sum} Connected Accounts`
    },
    connectedButtonColor () {
      if (!this.connectedAccounts || this.connectedAccounts.status === 'not_checked') return 'secondary'
      if (this.connectedAccounts.status === 'no_connected') return 'danger'
      if (this.connectedAccounts.sum <= 2) return 'warning'
      return 'success'
    },
    emailAgeText () {
      if (!this.emailBreaches || this.emailBreaches.count === 0) return 'Unknown'
      if (this.emailBreaches.count === 1 && this.emailBreaches.breach_list === 'NoBreach') return 'Unknown'
      // otherwise, we have a numeric email_age
      if (this.emailBreaches.email_age !== null) {
        return `${this.emailBreaches.email_age} old`
      }
      return 'Unknown'
    },
    emailAgeColorClass () {
      // If unknown => orange
      if (!this.emailBreaches || this.emailBreaches.count === 0) return 'warning'
      if (this.emailBreaches.count === 1 && this.emailBreaches.breach_list === 'NoBreach') return 'warning'

      if (this.emailBreaches.email_age) {
        // otherwise => we have a string email_age like "2 Years" or "5 Months"
        const yrs = parseInt(this.emailBreaches.email_age, 10)
        if (this.emailBreaches.email_age.includes('Years') && yrs >= 2) return 'success'
      }
      return 'warning'
    },
    limitedConnectedAccounts () {
      if (!this.relatedAccounts || !Array.isArray(this.relatedAccounts.results)) {
        return []
      }
      return this.relatedAccounts.results.slice(0, 6)
    },
    hasRelatedAccountsSummary () {
      return !!(this.relatedAccounts && this.relatedAccounts.summary)
    },
    modalRelatedAccountsResults () {
      if (!this.relatedAccountsModalData || !Array.isArray(this.relatedAccountsModalData.results)) {
        return []
      }
      return this.relatedAccountsModalData.results
    }
  },
  methods: {
    showUserProfilingBasics (userId) {
      this.$http
        .get(`/api/user-profiling/${userId}/basics`)
        .then((response) => {
          this.$refs['user_profiling_basics_modal'].show()
          this.userProfilingBasics = response.data.data
        })
        .catch((errors) => {
          if (errors && errors.response && errors.response.data) {
            this.alertError(errors.response.data.errors)
          }
        })
    },
    loadDefaultRelatedAccountsPreview () {
      if (!this.userId) return
      if (this.relatedAccounts && Array.isArray(this.relatedAccounts.results) && this.relatedAccounts.results.length > 0) return

      this.$http.get(`/api/user/${this.userId}/related-accounts`, {
        params: { days: DEFAULT_RELATED_ACCOUNT_DAYS }
      })
        .then((response) => {
          this.relatedAccounts = response?.data?.data || {}
        })
        .catch((error) => {
          console.error('Error loading related accounts preview', error)
        })
    },
    openRelatedAccountsModal (days = DEFAULT_RELATED_ACCOUNT_DAYS) {
      this.selectedRelatedAccountsDays = days
      this.$refs.user_related_account_data.show()
      this.fetchRelatedAccounts(days)
    },
    changeRelatedAccountsDays (days) {
      if (days === this.selectedRelatedAccountsDays && this.relatedAccountsModalData) return

      this.selectedRelatedAccountsDays = days
      this.fetchRelatedAccounts(days)
    },
    async fetchRelatedAccounts (days = this.selectedRelatedAccountsDays) {
      if (!this.userId) return

      const requestId = ++this.relatedAccountsModalRequestId
      this.isRelatedAccountsModalLoading = true
      this.relatedAccountsModalError = null

      try {
        const response = await this.$http.get(`/api/user/${this.userId}/related-accounts`, {
          params: { days }
        })

        if (requestId !== this.relatedAccountsModalRequestId) return

        this.relatedAccountsModalData = response?.data?.data || null
      } catch (error) {
        if (requestId !== this.relatedAccountsModalRequestId) return

        this.relatedAccountsModalData = null
        this.relatedAccountsModalError = error?.response?.data?.message || 'Failed to load related accounts.'
        console.error('Error loading related accounts', error)
      } finally {
        if (requestId === this.relatedAccountsModalRequestId) {
          this.isRelatedAccountsModalLoading = false
        }
      }
    },
    handleClickBreaches () {
      if (!this.user.hash) return

      if (!this.emailBreaches || this.emailBreaches.count === 0) {
        this.showLoading()

        this.$http.get(`/api/user/${this.user.hash}/check-breaches`)
          .then(response => {
            if (response.data) {
              this.emailBreaches = response.data
            }
          })
          .catch(err => {
            console.error('Error checking breaches', err)
          })
          .finally(() => {
            this.hideLoading()
          })
      } else {
        this.$refs.breachesModal.show()
      }
    },

    handleClickAccountsConnected () {
      if (!this.user.hash) return

      if (!this.connectedAccounts || this.connectedAccounts.status === 'not_checked') {
        this.showLoading()

        this.$http.get(`/api/user/${this.user.hash}/check-connected-accounts`)
          .then(response => {
            if (response.data) {
              this.connectedAccounts = response.data
            }
          })
          .catch(err => {
            console.error('Error checking connected accounts', err)
          })
          .finally(() => {
            this.hideLoading()
          })
      } else {
        this.$refs.accountsConnectedModal.show()
      }
    },
    parseArray (data) {
      if (Array.isArray(data)) {
        return data
      } else if (typeof data === 'object' && data !== null) {
        return Object.values(data)
      }
      return []
    },
    getClassVerificationDelay (delay) {
      const minutes = delay / 60

      if (minutes < 5) return 'bg-success text-white'
      if (minutes < 10) return 'bg-warning text-white'
      if (minutes >= 10) return 'bg-danger text-white'
    },
    getClassIpqsScore (score) {
      if (parseFloat(score) >= 80) return 'bg-danger text-white'
      if (parseFloat(score) > 50) return 'bg-warning text-white'
      if (parseFloat(score) < 50) return 'bg-success text-white'
    },
    getClassCarrierRisk (bannedPercentage) {
      const percent = Math.round(parseFloat(bannedPercentage) * 100)

      if (percent >= 60) return 'bg-danger text-white'
      if (percent >= 25 && percent < 60) return 'bg-warning text-white'

      return 'bg-success text-white'
    },
    getClassReconciliationPercentage (percentage) {
      if (!percentage || percentage.toFixed() < 5) return 'bg-success text-white'
      if (percentage.toFixed() > 10) return 'bg-danger text-white'

      return 'bg-warning text-white'
    },
    getClassBannedUserDuplicates (openEnd) {
      if (parseFloat(openEnd.banned_users_count_percentage) < 0.05) return 'bg-success text-white'
      if (
        parseFloat(openEnd.banned_users_count_percentage) >= 0.05 &&
          parseFloat(openEnd.banned_users_count_percentage) <= 0.2
      ) return 'bg-warning text-white'
      if (parseFloat(openEnd.banned_users_count_percentage) > 0.2) return 'bg-danger text-white'

      return ''
    },
    getConfidence (avgDaysString) {
      const avgDays = parseInt(avgDaysString)

      let text = ''
      let cssClass = ''

      if (avgDays >= 10) {
        text = 'High'
        cssClass = 'bg-success text-white'
      } else if (avgDays >= 7) {
        text = 'Medium'
        cssClass = 'bg-warning text-white'
      } else {
        text = 'Low'
        cssClass = 'bg-warning text-white'
      }

      return { text, cssClass }
    },
    getUserDetails () {
      const currentUserDetails =
          this.$store.getters['user/getCurrentUserDetails']

      this.loadError = null

      // If the user details exist in the store, use them
      // eslint-disable-next-line eqeqeq
      if (currentUserDetails && currentUserDetails.user.id == this.userId) {
        this.performance = currentUserDetails.performance
        this.quality_questions = currentUserDetails.quality_questions
        this.user = currentUserDetails.user
        this.user.org_comment = currentUserDetails.user.comment
        this.phone_verifications = currentUserDetails.phone_verifications
        this.reconciliation = currentUserDetails.reconciliation
        this.completes_and_conversion_rates =
            currentUserDetails.completes_and_conversion_rates_thirty_days_ago
        this.otherAccounts = currentUserDetails.other_accounts
        this.relatedAccounts = currentUserDetails.related_accounts
        this.languageCodes = currentUserDetails.language_codes
        this.isHighRiskEmailDomain = currentUserDetails.is_high_risk_email_domain
        this.platformLastHundredClicks = currentUserDetails.platform_last_100_clicks
        this.staffReviewer = currentUserDetails.staff_reviewer
        this.publisherPayoutChart = currentUserDetails.daily_payout_chart
        this.emailBreaches = currentUserDetails.email_breaches
        this.connectedAccounts = currentUserDetails.connected_accounts

        this.loadDefaultRelatedAccountsPreview()
      } else {
        if (this.hideFullPageSpinner) {
          this.isLoadingDetails = true
        } else {
          this.showLoading()
        }

        this.$http
          .get(`/api/user/${this.userId}`)
          .then((response) => {
            if (this.hideFullPageSpinner) {
              this.isLoadingDetails = false
            } else {
              this.hideLoading()
            }
            const returnData = response.data

            this.performance = returnData.performance
            this.quality_questions = returnData.quality_questions
            this.user = returnData.user
            this.user.org_comment = this.user.comment
            this.phone_verifications = returnData.phone_verifications
            this.reconciliation = returnData.reconciliation
            this.completes_and_conversion_rates =
                  returnData.completes_and_conversion_rates_thirty_days_ago
            this.otherAccounts = returnData.other_accounts
            this.relatedAccounts = returnData.related_accounts
            this.languageCodes = returnData.language_codes
            this.isHighRiskEmailDomain = returnData.is_high_risk_email_domain
            this.platformLastHundredClicks = returnData.platform_last_100_clicks
            this.staffReviewer = returnData.staff_reviewer
            this.publisherPayoutChart = returnData.daily_payout_chart
            this.emailBreaches = returnData.email_breaches
            this.connectedAccounts = returnData.connected_accounts

            this.loadDefaultRelatedAccountsPreview()

            // Save the user details in the Vuex store
            this.$store.commit('user/SET_CURRENT_USER_DETAIL', returnData)
          })
          .catch((errors) => {
            this.handleUserDetailsError(errors)
            if (this.hideFullPageSpinner) {
              this.isLoadingDetails = false
            } else {
              this.hideLoading()
            }
          })
      }
    },
    refreshUserDetails () {
      this.showLoading()
      this.$http
        .get(`/api/user/${this.userId}`)
        .then((response) => {
          this.hideLoading()
          const returnData = response.data
          this.performance = returnData.performance
          this.user = returnData.user
          this.user.org_comment = this.user.comment
          this.phone_verifications = returnData.phone_verifications
          this.$store.commit('user/SET_CURRENT_USER_DETAIL', returnData)
        })
        .catch((errors) => {
          this.hideLoading()
          this.handleUserDetailsError(errors)
        })
    },
    // A dead backend yields no CORS headers, so axios reports no `response` at all.
    handleUserDetailsError (errors) {
      const responseData = errors?.response?.data
      const status = errors?.response?.status
      let message = 'Could not load this user profile. The server did not return a response.'

      if (status) {
        message = `Could not load this user profile (error ${status}). Please try again.`
      }

      // Only take the page over when there is nothing already on screen.
      if (!this.user) {
        this.loadError = message
      }

      this.alertError(responseData || errors?.message || message)
    },
    setQuickLinkProperties () {
      store.commit('app/SET_QUICK_LINK_PROPERTIES', {
        display: true,
        id: this.id,
        project: 'ps',
        model: 'user'
      })
    },
    async getLifetimeReconciliations () {
      if (this.lifetimeReconciliation.length === 0) {
        this.isLoadingLifetime = true
        try {
          const {
            data: { lifetime_reconciliation }
          } = await this.$http.get(
            `/api/user/${this.userId}/lifetime-reconciliations`
          )
          this.lifetimeReconciliation = lifetime_reconciliation
        } catch (error) {
          console.error('Failed to load lifetime reconciliation', error)
        } finally {
          this.isLoadingLifetime = false
        }

        // Calculate total lifetime reconciliation %
        const totalCompletes = this.lifetimeReconciliation.reduce(
          (sum, item) => sum + item.completes_count,
          0
        )
        const totalReconciled = this.lifetimeReconciliation.reduce(
          (sum, item) => sum + item.reconciled_count,
          0
        )
        this.totalLifetimePercentage = totalCompletes ? ((totalReconciled / totalCompletes) * 100).toFixed(2) : '0'
      }
    }
  },
  watch: {
    '$store.state.chat.userDetailsInfo.ip_location_check': {
      handler (value) {
        if (this.user) {
          this.user.ip_location_check = value
        }
      }
    },
    '$route.params.userId' () {
      this.$router.go()
    },
    'user.status' (val) {
      this.$emit('update-user-status', val)
      if (this.status) {
        this.$emit('update:status', val)
      }
    },
    'shownAsModal': {
      handler (status) {
        if (status) {
          this.setQuickLinkProperties()
        }
      },
      immediate: true
    }
  },
  created () {
    this.getUserDetails()
    this.getLifetimeReconciliations()
  },
  mounted () {
    this.primeOpinionLogo = $themeConfig.app.appLogoImage
    this.fiveSurveysLogo = $themeConfig.app.fiveSurvesLogoImage
    this.heyCashLogo = $themeConfig.app.heyCashLogoImage
    this.topSurveysLogo = $themeConfig.app.topSurveysLogoImage
    this.topSurveysLogo = $themeConfig.app.topSurveysLogoImage
    this.earnStarLogo = $themeConfig.app.earnStarLogoImage
    this.paidTesterLogo = $themeConfig.app.paidTesterLogoImage

  }
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/vue/libs/vue-select.scss";

.inner-spinner {
  position: absolute;
  z-index: 997;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.5);

  ::v-deep {
    span {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50% -50%);
    }
  }
}
</style>
